import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/Button";
import { useI18n } from "../../../lib/i18n";
import { ApiError, brands, type Brand } from "../../../lib/api";

// /app/brands — list view. Fetches once on mount; calls go through
// api-gateway -> brand-service. The gateway injects X-Workspace-Id
// from the validated session, so this is just `GET /v1/brands`.
function BrandsList() {
  const { t } = useI18n();
  const [state, setState] = useState<
    { kind: "loading" } | { kind: "ready"; list: Brand[] } | { kind: "error"; msg: string }
  >({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    brands
      .list()
      .then((r) => {
        if (!cancelled) setState({ kind: "ready", list: r.brands });
      })
      .catch((e) => {
        if (cancelled) return;
        const msg = e instanceof ApiError ? `HTTP ${e.status}: ${e.message}` : "network error";
        setState({ kind: "error", msg });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 sm:py-12 flex flex-col gap-6">
      <div className="flex items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <span className="eyebrow">{t("brands.eyebrow")}</span>
          <h1 className="font-display text-3xl font-semibold">{t("brands.title")}</h1>
        </div>
        <Link to="/app/brands/new">
          <Button variant="primary">+ {t("brands.new")}</Button>
        </Link>
      </div>

      {state.kind === "loading" ? (
        <Card><p className="text-secondary">{t("common.loading")}</p></Card>
      ) : state.kind === "error" ? (
        <Card><p className="text-error">{state.msg}</p></Card>
      ) : state.list.length === 0 ? (
        <Card>
          <div className="flex flex-col items-center text-center gap-3 py-8">
            <span className="eyebrow eyebrow-pill inline-flex">EMPTY · BOŞ</span>
            <h2 className="font-display text-xl font-semibold">{t("brands.empty.title")}</h2>
            <p className="text-secondary text-sm max-w-md">{t("brands.empty.subtitle")}</p>
            <Link to="/app/brands/new" className="mt-2">
              <Button variant="primary">+ {t("brands.new")}</Button>
            </Link>
          </div>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {state.list.map((b) => (
            <Link
              key={b.id}
              to="/app/brands/$brandId"
              params={{ brandId: b.id }}
              className="ring-nexus rounded-[14px]"
            >
              <Card className="!p-5 hover:bg-overlay/40 transition h-full">
                <div className="flex items-baseline justify-between gap-2 mb-3">
                  <span className="eyebrow">{b.country} · {b.primary_language}</span>
                  {b.status !== "active" ? (
                    <span className="font-mono text-[11px] uppercase text-warning">{b.status}</span>
                  ) : null}
                </div>
                <h2 className="font-display text-lg font-semibold truncate" title={b.trade_name}>
                  {b.trade_name}
                </h2>
                <p className="text-xs text-muted font-mono mt-1 truncate" title={b.slug}>
                  /{b.slug}
                </p>
                {b.industry || b.category ? (
                  <p className="text-xs text-secondary mt-3 truncate">
                    {[b.industry, b.category].filter(Boolean).join(" · ")}
                  </p>
                ) : null}
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/app/brands/")({
  component: BrandsList,
});
